import {
  useMutation,
  type UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';

import { type UUID } from '~/types/common';

import ToastService from '~/services/toast.service';

import { i18n } from '~/utils';

import { queryKeysInvoiceReprocessing } from '../queryKeys';

import { reprocessInvoice } from './reprocessInvoice';
import { type ReprocessInvoiceResponse } from './types';

/**
 * A custom hook to reprocess an invoice with a given process ID.
 * Uses the `useMutation` hook from `@tanstack/react-query` internally.
 * @see https://app.dev.vestigas.com/redoc#tag/Invoice/operation/reprocess_invoice_invoice_process__process_id__reprocess_put
 */
export function useMutationReprocessInvoice(
  options?: UseMutationOptions<ReprocessInvoiceResponse, unknown, UUID>,
) {
  const queryClient = useQueryClient();

  return useMutation<ReprocessInvoiceResponse, unknown, UUID>({
    mutationFn: async (processId: UUID) => reprocessInvoice(processId),
    onError(_error) {
      return; // prevent error from bubbling up further unless overridden via mutation options
    },
    onSettled(_data, _error, processId) {
      void queryClient.invalidateQueries({
        queryKey: queryKeysInvoiceReprocessing.getAll({}),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeysInvoiceReprocessing.get(processId),
      });
    },
    onSuccess() {
      ToastService.info(
        i18n.t(($) => $.reprocessInvoice.success, {
          ns: 'data',
        }),
      );
    },
    ...options,
  });
}
